import { cn } from '@/lib/utils';
import { useReducedMotion } from 'framer-motion';
import { CSSProperties, ReactNode } from 'react';

/**
 * A continuously scrolling ribbon.
 *
 * The list is rendered twice and the track translates exactly one copy's width
 * (-50%), so the loop is seamless without measuring anything. Under reduced
 * motion it stops moving and simply wraps, so nothing is unreachable.
 */
const Marquee = ({
  items,
  separator = '✕',
  durationSeconds = 40,
  className,
}: {
  items: string[];
  separator?: ReactNode;
  durationSeconds?: number;
  className?: string;
}) => {
  const reduced = useReducedMotion();

  if (items.length === 0) {
    return null;
  }

  const row = (key: string, hidden: boolean) => (
    <ul
      key={key}
      aria-hidden={hidden}
      className={cn(
        'flex shrink-0 items-center gap-8 px-4',
        reduced && 'flex-wrap justify-center'
      )}
    >
      {items.map((item, index) => (
        <li key={`${item}-${index}`} className="flex items-center gap-8">
          <span className="whitespace-nowrap text-sm font-bold uppercase tracking-[0.14em]">
            {item}
          </span>
          {/* Inherits the ribbon's own foreground rather than setting
              text-primary, which on the home page put orange separators on an
              orange band — invisible since the ribbon was built. */}
          <span aria-hidden className="opacity-50">
            {separator}
          </span>
        </li>
      ))}
    </ul>
  );

  if (reduced) {
    return (
      <div className={cn('w-full py-3', className)}>{row('a', false)}</div>
    );
  }

  return (
    <div
      className={cn('w-full overflow-hidden py-3', className)}
      style={{ '--marquee-duration': `${durationSeconds}s` } as CSSProperties}
    >
      <div className="flex w-max animate-marquee">
        {row('a', false)}
        {row('b', true)}
      </div>
    </div>
  );
};

export default Marquee;
